import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, Search, Filter, ChevronRight, Building2, Briefcase, TrendingUp, UserCheck, GraduationCap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const DEPT_COLORS = {
  "Computer Science & Engineering": "bg-blue-500",
  "Electronics & Communication Engineering": "bg-purple-500",
  "Mechanical Engineering": "bg-orange-500",
  "Civil Engineering": "bg-green-500",
  "Administration & Finance": "bg-slate-500",
};

const JOB_FAMILY_LABELS = {
  TEACHING_FACULTY: "Teaching Faculty",
  TECHNICAL_SUPPORT: "Technical Staff",
  FINANCE_ADMINISTRATION: "Finance",
  HR_ADMINISTRATION: "HR",
};

const STATUS_BADGE = {
  ACTIVE: "bg-emerald-100 text-emerald-700 border border-emerald-200",
  INACTIVE: "bg-yellow-100 text-yellow-700 border border-yellow-200",
  TERMINATED: "bg-red-100 text-red-700 border border-red-200",
};

export default function PeopleDirectory() {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [stats, setStats] = useState(null);
  const [deptSummary, setDeptSummary] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [filterFamily, setFilterFamily] = useState("");
  const [total, setTotal] = useState(0);

  const token = localStorage.getItem("academiaos_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, [filterDept, filterFamily]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchWorkers(), 300);
    return () => clearTimeout(debounce);
  }, [search, filterDept, filterFamily]);

  const fetchData = async () => {
    try {
      const [statsRes, deptRes, ouRes] = await Promise.all([
        axios.get(`${API}/api/v1/peo01/stats`, { headers }),
        axios.get(`${API}/api/v1/peo01/department-summary`, { headers }),
        axios.get(`${API}/api/v1/peo01/org-units`, { headers }),
      ]);
      setStats(statsRes.data);
      setDeptSummary(deptRes.data);
      setOrgUnits(ouRes.data.org_units || []);
    } catch (e) {
      // stats are optional; don't block the page
    }
  };

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const params = { limit: 50, offset: 0 };
      if (search) params.search = search;
      if (filterDept) params.department_id = filterDept;
      if (filterFamily) params.job_family = filterFamily;

      const res = await axios.get(`${API}/api/v1/peo01/workers`, { headers, params });
      setWorkers(res.data.workers || []);
      setTotal(res.data.total || 0);
      setError(null);
    } catch (e) {
      if (e.response?.status === 403) {
        setError("You do not have permission to view the worker directory.");
      } else {
        setError("Failed to load workers. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) =>
    name?.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase() || "??";

  const getJobFamilyLabel = (family) => JOB_FAMILY_LABELS[family] || family || "Staff";

  const deptOptions = orgUnits.filter((ou) => ou.unit_type === "DEPARTMENT" || ou.unit_type === "ADMIN");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Users size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">People Directory</h1>
              <p className="text-sm text-slate-500">
                Vaagdevi College of Engineering — Employment Foundation
                <span className="ml-2 text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium">
                  Synthetic Demo
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8 space-y-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="peo01-stats">
            <StatCard icon={<Users size={20} className="text-indigo-500" />} label="Active Workers" value={stats.active_workers} color="indigo" />
            <StatCard icon={<GraduationCap size={20} className="text-blue-500" />} label="Teaching Faculty" value={stats.teaching_faculty} color="blue" />
            <StatCard icon={<UserCheck size={20} className="text-green-500" />} label="Non-Teaching Staff" value={stats.non_teaching_staff} color="green" />
            <StatCard icon={<Building2 size={20} className="text-purple-500" />} label="Departments" value={stats.departments} color="purple" />
          </div>
        )}

        {/* Dept Summary */}
        {deptSummary.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <Building2 size={15} /> Department Headcount
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {deptSummary.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setFilterDept(filterDept === d.id ? "" : d.id)}
                  data-testid={`dept-filter-${d.code}`}
                  className={`rounded-lg p-3 text-left border transition-all ${
                    filterDept === d.id
                      ? "bg-indigo-50 border-indigo-300 ring-1 ring-indigo-300"
                      : "bg-slate-50 border-slate-200 hover:bg-white hover:border-indigo-200"
                  }`}
                >
                  <div className={`w-6 h-1.5 rounded-full mb-2 ${DEPT_COLORS[d.name] || "bg-slate-400"}`} />
                  <div className="text-xl font-bold text-slate-900">{d.headcount}</div>
                  <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{d.name}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name or employee ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="worker-search"
              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none bg-white"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filterFamily}
              onChange={(e) => setFilterFamily(e.target.value)}
              data-testid="worker-filter-family"
              className="border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 bg-white focus:ring-2 focus:ring-indigo-300 outline-none"
            >
              <option value="">All Categories</option>
              <option value="TEACHING_FACULTY">Teaching Faculty</option>
              <option value="TECHNICAL_SUPPORT">Technical Staff</option>
              <option value="FINANCE_ADMINISTRATION">Finance</option>
              <option value="HR_ADMINISTRATION">HR</option>
            </select>
            {(filterDept || filterFamily || search) && (
              <button
                onClick={() => { setSearch(""); setFilterDept(""); setFilterFamily(""); }}
                data-testid="clear-filters"
                className="px-3 py-2.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-100 bg-white"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Worker list */}
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center text-red-700 text-sm" data-testid="peo01-error">
            {error}
          </div>
        ) : loading ? (
          <div className="space-y-3" data-testid="peo01-loading">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-white rounded-xl border border-slate-200 animate-pulse" />
            ))}
          </div>
        ) : workers.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="peo01-empty">
            <Users size={40} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No workers match the current filters.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="peo01-worker-list">
            <p className="text-xs text-slate-500 font-medium">{total} worker{total !== 1 ? "s" : ""} found</p>
            {workers.map((w) => (
              <WorkerCard key={w.worker_id} worker={w} onView={() => navigate(`/people/employment/${w.worker_id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colorMap = {
    indigo: "bg-indigo-50 border-indigo-100",
    blue: "bg-blue-50 border-blue-100",
    green: "bg-green-50 border-green-100",
    purple: "bg-purple-50 border-purple-100",
  };
  return (
    <div className={`rounded-xl border p-4 ${colorMap[color]}`}>
      <div className="flex items-center gap-2 mb-2">{icon}<span className="text-xs text-slate-600 font-medium">{label}</span></div>
      <div className="text-3xl font-bold text-slate-900">{value ?? "—"}</div>
    </div>
  );
}

function WorkerCard({ worker: w, onView }) {
  const initials = w.display_name?.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase() || "??";
  const deptColor = DEPT_COLORS[w.department_name] || "bg-slate-400";

  return (
    <div
      onClick={onView}
      data-testid={`worker-card-${w.worker_number}`}
      className="bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer p-4 flex items-center gap-4"
    >
      {/* Avatar */}
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${deptColor}`}>
        {initials}
      </div>

      {/* Main info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="font-semibold text-slate-900 text-sm truncate">{w.display_name}</span>
          {w.is_synthetic && (
            <span className="text-xs bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded-full flex-shrink-0">Demo</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="font-mono">{w.worker_number}</span>
          {w.job_title && (
            <span className="flex items-center gap-1">
              <Briefcase size={10} />
              {w.job_title}
            </span>
          )}
          {w.department_name && (
            <span className="flex items-center gap-1 hidden sm:flex">
              <Building2 size={10} />
              {w.department_name}
            </span>
          )}
        </div>
      </div>

      {/* Right column */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="hidden sm:flex flex-col items-end gap-1">
          {w.grade_name && (
            <span className="text-xs text-slate-500">{w.grade_name.split("—")[1]?.trim()}</span>
          )}
          {w.employment_status && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[w.employment_status] || ""}`}>
              {w.employment_status}
            </span>
          )}
        </div>
        <ChevronRight size={16} className="text-slate-400" />
      </div>
    </div>
  );
}
